import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  UsersIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  UserIcon,
  ShieldCheckIcon,
  AcademicCapIcon,
  BuildingOfficeIcon,
  XCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  UserCircleIcon,
  ArrowPathIcon,
  LockClosedIcon,
  LockOpenIcon
} from '@heroicons/react/24/outline'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import PhoneNumberInput from '@/components/ui/PhoneNumberInput'
import { useAuth } from '@/hooks/useAuth'
import { 
  getUsers, 
  createUser, 
  updateUser, 
  deleteUser, 
  getUserStats, 
  type User as ApiUser
} from '@/api/usersApi'
import { getRoles } from '@/api/rolesApi'
import toast from 'react-hot-toast'

// Form schema for adding new user
const addUserSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().email('Valid email address is required'),
  phone: z.string().optional(),
  countryCode: z.string().optional(),
  roleId: z.string().min(1, 'Role is required'),
  institutionId: z.string().optional() // For super admins
})

type AddUserData = z.infer<typeof addUserSchema>

const UsersPage: React.FC = () => {
  const { user } = useAuth()
  const [users, setUsers] = useState<ApiUser[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [roleFilter, setRoleFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [institutionFilter, setInstitutionFilter] = useState<string>('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<ApiUser | null>(null)
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table')
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalUsers, setTotalUsers] = useState(0)
  const [pageSize] = useState(10)
  // Sorting state
  const [sortBy] = useState<'createdAt' | 'firstName' | 'lastName' | 'email' | 'lastLogin'>('createdAt')
  const [sortOrder] = useState<'asc' | 'desc'>('desc')
  // Available roles from API
  const [availableRoles, setAvailableRoles] = useState<{ id: string, name: string }[]>([])
  // Available institutions (for super admin)
  const [institutions, setInstitutions] = useState<{ id: string, name: string }[]>([])
  // Stats state - moved to top to maintain hook order
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, byRole: {} as Record<string, number> })

  const addUserForm = useForm<AddUserData>({
    resolver: zodResolver(addUserSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      countryCode: 'KE',
      roleId: '',
      institutionId: ''
    }
  })

  // Move all fetch functions before useEffect
  const fetchInstitutions = async () => {
    try {
      // For now, use mock data since getInstitutions might not be available
      setInstitutions([
        { id: '1', name: 'Greenwood Academy' },
        { id: '2', name: 'Oak Hill International' },
        { id: '3', name: 'Sunrise Primary School' }
      ])
    } catch (error) {
      console.error('Failed to fetch institutions:', error)
    }
  }

  const fetchUsers = async () => {
    setLoading(true)
    try {
      const response = await getUsers({
        page: currentPage,
        limit: pageSize,
        search: searchQuery || undefined,
        roleId: roleFilter !== 'all' ? roleFilter : undefined,
        status: statusFilter !== 'all' ? statusFilter as 'active' | 'inactive' : undefined,
        sortBy,
        sortOrder,
        institutionId: user?.role === 'SUPER_ADMIN' ? institutionFilter !== 'all' ? institutionFilter : undefined : undefined
      })
      
      console.log('API Response:', response) // Debug log
      
      if (response && response.users) {
        setUsers(response.users)
        if (response.pagination) {
          setCurrentPage(response.pagination.currentPage)
          setTotalPages(response.pagination.totalPages)
          setTotalUsers(response.pagination.totalUsers)
        }
      }
    } catch (error) {
      console.error('Failed to fetch users:', error)
      toast.error('Failed to load users')
    } finally {
      setLoading(false)
    }
  }

  const fetchRoles = async () => {
    try {
      const response = await getRoles()
      setAvailableRoles(response.data.map(role => ({ id: role.id, name: role.name })))
    } catch (error) {
      console.error('Failed to fetch roles:', error)
    }
  }

  const fetchStats = async () => {
    try {
      const response = await getUserStats(
        user?.role === 'SUPER_ADMIN' ? institutionFilter !== 'all' ? institutionFilter : undefined : undefined
      )
      // Extract group counts from the response (all keys except basic counts)
      const { totalUsers, activeUsers, inactiveUsers, ...groupCounts } = response
      setStats({
        total: totalUsers,
        active: activeUsers,
        inactive: inactiveUsers,
        byRole: groupCounts
      })
    } catch (error) {
      console.error('Failed to fetch stats:', error)
    }
  }

  useEffect(() => {
    fetchUsers()
    fetchRoles()
    fetchStats()
    if (user?.role === 'SUPER_ADMIN') {
      fetchInstitutions()
    }
  }, [currentPage, searchQuery, roleFilter, statusFilter, sortBy, sortOrder, institutionFilter])

  // Debounced search to avoid too many API calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCurrentPage(1) // Reset to first page when filters change
    }, 300)
    
    return () => clearTimeout(timeoutId)
  }, [searchQuery, roleFilter, statusFilter, institutionFilter])

  // Handle pagination change
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  // Handle sort change
  // const handleSort = (field: typeof sortBy) => {
  //   if (field === sortBy) {
  //     setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
  //   } else {
  //     setSortBy(field)
  //     setSortOrder('asc')
  //   }
  // }

  const getRoleBadge = (user: ApiUser) => {
    const roleName = user.role || 'Unknown'
    
    // Simple color assignment based on role name
    const getColorClass = (name: string) => {
      const lowerName = name.toLowerCase()
      if (lowerName.includes('admin')) return 'badge-warning'
      if (lowerName.includes('teacher')) return 'badge-primary'
      if (lowerName.includes('parent')) return 'badge-success'
      if (lowerName.includes('driver')) return 'badge-gray'
      if (lowerName.includes('student')) return 'badge bg-purple-100 text-purple-800'
      return 'badge-gray'
    }
    
    return (
      <span className={`badge ${getColorClass(roleName)}`}>
        {roleName}
      </span>
    )
  }

  const getStatusBadge = (user: ApiUser) => {
    const isActive = user.status
    const status = isActive === "Active" ? 'active' : 'inactive'
    
    const styles = {
      active: 'badge-success',
      inactive: 'badge-gray'
    }
    
    const labels = {
      active: 'Active',
      inactive: 'Inactive'
    }
    
    return (
      <span className={`badge ${styles[status]}`}>
        {labels[status]}
      </span>
    )
  }

  const getRoleIcon = (roleName?: string) => {
    const iconClass = "h-5 w-5"
    if (!roleName) return <UserIcon className={`${iconClass} text-gray-600`} />
    
    const lowerName = roleName.toLowerCase()
    if (lowerName.includes('admin')) {
      return lowerName.includes('super') 
        ? <ShieldCheckIcon className={`${iconClass} text-red-600`} />
        : <BuildingOfficeIcon className={`${iconClass} text-orange-600`} />
    }
    if (lowerName.includes('teacher')) return <AcademicCapIcon className={`${iconClass} text-blue-600`} />
    if (lowerName.includes('parent')) return <UsersIcon className={`${iconClass} text-green-600`} />
    if (lowerName.includes('driver')) return <MapPinIcon className={`${iconClass} text-gray-600`} />
    if (lowerName.includes('student')) return <UserIcon className={`${iconClass} text-purple-600`} />
    return <UserIcon className={`${iconClass} text-gray-600`} />
  }

  const handleViewUser = (user: ApiUser) => {
    setSelectedUser(user)
  }

  const handleEditUser = (user: ApiUser) => {
    toast.success(`Edit ${user.firstName} ${user.lastName} - Coming soon!`)
  }

  const handleDeleteUser = async (user: ApiUser) => {
    if (confirm(`Are you sure you want to delete ${user.firstName} ${user.lastName}?`)) {
      try {
        await deleteUser(user.id)
        toast.success(`${user.firstName} ${user.lastName} deleted successfully`)
        fetchUsers()
      } catch (error) {
        toast.error('Failed to delete user')
      }
    }
  }

  const handleToggleStatus = async (user: ApiUser) => {
    try {
      const newIsActive = user.status === 'active' ? false : true
      await updateUser(user.id, { isActive: newIsActive })
      toast.success(`${user.firstName} ${user.lastName} ${newIsActive ? 'activated' : 'deactivated'}`)
      fetchUsers()
    } catch (error) {
      toast.error('Failed to update user status')
    }
  }

  const handleResetPassword = (user: ApiUser) => {
    toast.success(`Password reset email sent to ${user.email}`)
  }

  const handleAddUser = async (userData: AddUserData) => {
    try {
      const response = await createUser({
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        roleId: userData.roleId,
        phone: userData.phone,
        countryCode: userData.countryCode,
        institutionId: userData.institutionId
        // password will be auto-generated by backend
      })
      
      // Show success message with login credentials if available
      const tempPassword = response.temporaryPassword
      if (tempPassword) {
        toast.success(
          <div className="space-y-2">
            <p className="font-semibold">{userData.firstName} {userData.lastName} created successfully!</p>
            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
              <p className="font-medium text-blue-800">Login Credentials:</p>
              <p><span className="font-medium">Email:</span> {userData.email}</p>
              <p><span className="font-medium">Password:</span> {tempPassword}</p>
              <p className="text-blue-600 text-xs mt-2">
                🔒 Please share these credentials securely. They can change their password after first login.
              </p>
            </div>
          </div>,
          { 
            duration: 10000, // Show for 10 seconds
            position: 'top-center'
          }
        )
      } else {
        toast.success(`${userData.firstName} ${userData.lastName} created successfully!`)
      }
      
      setShowAddModal(false)
      addUserForm.reset()
      fetchUsers() // Refresh the list
      
    } catch (error: any) {
      console.error('Failed to create user:', error)
      const message = error?.response?.data?.message || 'Failed to create user. Please try again.'
      toast.error(message)
    }
  }

  const UserCard = ({ user }: { user: ApiUser }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="dashboard-card hover:shadow-large transition-all duration-200"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center">
            {user.profileImage ? (
              <img
                src={user.profileImage}
                alt={`${user.firstName} ${user.lastName}`}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              <UserCircleIcon className="h-8 w-8 text-gray-500" />
            )}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {user.firstName} {user.lastName}
            </h3>
            <p className="text-sm text-gray-500">{user.institution || 'No Institution'}</p>
          </div>
        </div>
        <div className="flex flex-col items-end space-y-2">
          {getRoleBadge(user.role)}
          {getStatusBadge(user)}
        </div>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <EnvelopeIcon className="h-4 w-4" />
          <span>{user.email}</span>
          {user.emailVerified && (
            <CheckCircleIcon className="h-4 w-4 text-success-500" />
          )}
        </div>
        {user.phone && (
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <PhoneIcon className="h-4 w-4" />
            <span>{user.phone}</span>
          </div>
        )}
        {user.lastLogin && (
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <ClockIcon className="h-4 w-4" />
            <span>Last login: {user.lastLogin}</span>
          </div>
        )}
      </div>

     

      <div className="flex items-center justify-between">
        <div className="flex space-x-2">
          <button
            onClick={() => handleViewUser(user)}
            className="btn btn-ghost text-xs"
          >
            <EyeIcon className="h-4 w-4 mr-1" />
            View
          </button>
          <button
            onClick={() => handleEditUser(user)}
            className="btn btn-ghost text-xs"
          >
            <PencilIcon className="h-4 w-4 mr-1" />
            Edit
          </button>
          <button
            onClick={() => handleToggleStatus(user)}
            className={`btn btn-ghost text-xs ${user.status === 'active' ? 'text-orange-600 hover:bg-orange-50' : 'text-green-600 hover:bg-green-50'}`}
          >
            {user.status === 'active' ? (
              <>
                <LockClosedIcon className="h-4 w-4 mr-1" />
                Deactivate
              </>
            ) : (
              <>
                <LockOpenIcon className="h-4 w-4 mr-1" />
                Activate
              </>
            )}
          </button>
        </div>
        <div className="flex items-center space-x-1">
          {getRoleIcon(user.role)}
        </div>
      </div>
    </motion.div>
  )

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 bg-gray-200 rounded w-48 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-96 mt-2 animate-pulse"></div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="dashboard-stat-card animate-pulse">
              <div className="h-16 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
        <div className="dashboard-card animate-pulse">
          <div className="h-96 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  // Stats are now handled by the useState hook at the top of the component

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users Management</h1>
          <p className="text-gray-600 mt-1">
            Manage all platform users across all institutions
          </p>
        </div>
        <button 
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add User
        </button>
      </div>

      {/* Stats Row - Dynamic based on actual groups */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
        {/* Core Stats */}
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total?.toLocaleString() || 0}</p>
            </div>
            <UsersIcon className="h-8 w-8 text-primary-600" />
          </div>
        </div>
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Users</p>
              <p className="text-2xl font-bold text-gray-900">{stats.active || 0}</p>
            </div>
            <CheckCircleIcon className="h-8 w-8 text-success-600" />
          </div>
        </div>
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Inactive Users</p>
              <p className="text-2xl font-bold text-gray-900">{stats.inactive || 0}</p>
            </div>
            <XCircleIcon className="h-8 w-8 text-red-500" />
          </div>
        </div>
        
        {/* Dynamic Role/Group Stats */}
        {Object.entries(stats.byRole || {}).map(([groupName, count]) => (
          <div key={groupName} className="dashboard-stat-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{groupName}</p>
                <p className="text-2xl font-bold text-gray-900">{count}</p>
              </div>
              <UserIcon className="h-8 w-8 text-blue-600" />
            </div>
          </div>
        ))}
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
        <div className="flex-1 relative max-w-md">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="form-input pl-10"
          />
        </div>
        
        <div className="flex items-center space-x-3">
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="form-input text-sm"
          >
            <option value="all">All Roles</option>
            {availableRoles.map(role => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </select>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="form-input text-sm"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <select
            value={institutionFilter}
            onChange={(e) => setInstitutionFilter(e.target.value)}
            className="form-input text-sm"
          >
            <option value="all">All Institutions</option>
            {institutions.map(institution => (
              <option key={institution.id} value={institution.id}>
                {institution.name}
              </option>
            ))}
          </select>

          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded text-sm ${viewMode === 'table' ? 'bg-white shadow-sm text-primary-600' : 'text-gray-600'}`}
            >
              Table
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded text-sm ${viewMode === 'grid' ? 'bg-white shadow-sm text-primary-600' : 'text-gray-600'}`}
            >
              Grid
            </button>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalUsers)} of {totalUsers} users</span>
        <div className="flex items-center space-x-3">
          <button 
            onClick={() => toast.success('Bulk actions coming soon!')}
            className="btn btn-ghost text-sm"
          >
            Bulk Actions
          </button>
          <button 
            onClick={() => toast.success('Export feature coming soon!')}
            className="btn btn-ghost text-sm"
          >
            Export Data
          </button>
        </div>
      </div>

      {/* Users Table */}
      {viewMode === 'table' && (
        <div className="dashboard-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Institution
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Login
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mr-3">
                          {user.profileImage ? (
                            <img
                              src={user.profileImage}
                              alt={`${user.firstName} ${user.lastName}`}
                              className="w-full h-full rounded-full object-cover"
                            />
                          ) : (
                            <UserCircleIcon className="h-6 w-6 text-gray-500" />
                          )}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center space-x-1">
                            <span>{user.email}</span>
                            {user.emailVerified && (
                              <CheckCircleIcon className="h-3 w-3 text-success-500" />
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {getRoleIcon(user.role)}
                        {getRoleBadge(user)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.institution || 'No Institution'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(user)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.lastLogin || 'Never'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleViewUser(user)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="View Details"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEditUser(user)}
                          className="text-gray-600 hover:text-gray-900"
                          title="Edit User"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleResetPassword(user)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Reset Password"
                        >
                          <ArrowPathIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(user)}
                          className={user.status === 'active' ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'}
                          title={user.status === 'active' ? 'Deactivate' : 'Activate'}
                        >
                          {user.status === 'active' ? (
                            <LockClosedIcon className="h-4 w-4" />
                          ) : (
                            <LockOpenIcon className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete User"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="dashboard-card">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="btn btn-secondary btn-sm disabled:opacity-50"
              >
                Previous
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum
                if (totalPages <= 5) {
                  pageNum = i + 1
                } else if (currentPage <= 3) {
                  pageNum = i + 1
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i
                } else {
                  pageNum = currentPage - 2 + i
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`btn btn-sm ${pageNum === currentPage ? 'btn-primary' : 'btn-secondary'}`}
                  >
                    {pageNum}
                  </button>
                )
              })}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="btn btn-secondary btn-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Users Grid */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence>
            {users.map((user) => (
              <UserCard key={user.id} user={user} />
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Empty State */}
      {users.length === 0 && !loading && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="dashboard-card"
        >
          <div className="text-center py-12">
            <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No users found</h3>
            <p className="mt-2 text-gray-500">
              {searchQuery || roleFilter !== 'all' || statusFilter !== 'all' || institutionFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Get started by adding your first user to the platform.'
              }
            </p>
            {(!searchQuery && roleFilter === 'all' && statusFilter === 'all' && institutionFilter === 'all') && (
              <button 
                onClick={() => setShowAddModal(true)}
                className="mt-4 btn btn-primary"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add First User
              </button>
            )}
          </div>
        </motion.div>
      )}

      {/* Add User Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Add New User</h3>
                    <p className="text-sm text-gray-500">Create a new user account</p>
                  </div>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <form onSubmit={addUserForm.handleSubmit(handleAddUser)} className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    {...addUserForm.register('firstName')}
                    label="First Name"
                    placeholder="e.g., John"
                    error={addUserForm.formState.errors.firstName?.message}
                  />

                  <Input
                    {...addUserForm.register('lastName')}
                    label="Last Name"
                    placeholder="e.g., Smith"
                    error={addUserForm.formState.errors.lastName?.message}
                  />

                  <Input
                    {...addUserForm.register('email')}
                    type="email"
                    label="Email Address"
                    placeholder="e.g., j.smith@example.com"
                    error={addUserForm.formState.errors.email?.message}
                  />

                  {/* Password is auto-generated by backend */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-center">
                      <LockClosedIcon className="h-5 w-5 text-blue-600 mr-2" />
                      <div>
                        <p className="text-sm font-medium text-blue-800">Secure Password</p>
                        <p className="text-xs text-blue-600">Auto-generated and sent via email</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number (Optional)
                    </label>
                    <PhoneNumberInput
                      value={addUserForm.watch('phone') || ''}
                      countryCode={addUserForm.watch('countryCode') || 'KE'}
                      onChange={(phone, countryCode) => {
                        addUserForm.setValue('phone', phone)
                        addUserForm.setValue('countryCode', countryCode)
                      }}
                      placeholder="Enter phone number"
                      error={addUserForm.formState.errors.phone?.message}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      User Role
                    </label>
                    <select
                      {...addUserForm.register('roleId')}
                      className="form-input"
                    >
                      <option value="">Select Role</option>
                      {availableRoles.map(role => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                    {addUserForm.formState.errors.roleId && (
                      <p className="mt-1 text-sm text-red-600">{addUserForm.formState.errors.roleId.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Institution
                    </label>
                    <select
                      {...addUserForm.register('institutionId')}
                      className="form-input"
                    >
                      <option value="">Select Institution</option>
                      {institutions.map(institution => (
                        <option key={institution.id} value={institution.id}>
                          {institution.name}
                        </option>
                      ))}
                    </select>
                    {addUserForm.formState.errors.institutionId && (
                      <p className="mt-1 text-sm text-red-600">{addUserForm.formState.errors.institutionId.message}</p>
                    )}
                  </div>
                </div>

                {/* Default Settings Notice */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircleIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <h5 className="text-sm font-medium text-blue-900">Default Settings</h5>
                      <div className="text-sm text-blue-700 mt-1 space-y-1">
                        <p>• A secure password will be automatically generated</p>
                        <p>• Welcome email with login credentials will be sent</p>
                        <p>• User account will be activated immediately</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t">
                  <Button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-primary-600 hover:bg-primary-700"
                  >
                    Create User
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* User Details Modal */}
      <AnimatePresence>
        {selectedUser && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedUser(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center">
                      {selectedUser.profileImage ? (
                        <img
                          src={selectedUser.profileImage}
                          alt={`${selectedUser.firstName} ${selectedUser.lastName}`}
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        <UserCircleIcon className="h-10 w-10 text-gray-500" />
                      )}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">
                        {selectedUser.firstName} {selectedUser.lastName}
                      </h3>
                      <p className="text-sm text-gray-500">{selectedUser.institution || 'No Institution'}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedUser(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* User Info Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Contact Information */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">Contact Information</h4>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm">{selectedUser.email}</span>
                        {selectedUser.emailVerified && (
                          <CheckCircleIcon className="h-4 w-4 text-success-500" />
                        )}
                      </div>
                      {selectedUser.phone && (
                        <div className="flex items-center space-x-3">
                          <PhoneIcon className="h-4 w-4 text-gray-400" />
                          <span className="text-sm">{selectedUser.phone}</span>
                        </div>
                      )}
                      {selectedUser.institution && (
                        <div className="flex items-center space-x-3">
                          <BuildingOfficeIcon className="h-4 w-4 text-gray-400" />
                          <span className="text-sm">{selectedUser.institution}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Account Information */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">Account Information</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Role</span>
                        <div className="flex items-center space-x-2">
                          {getRoleIcon(selectedUser.role)}
                          {getRoleBadge(selectedUser)}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Status</span>
                        {getStatusBadge(selectedUser)}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Member Since</span>
                        <span className="text-sm text-gray-900">{selectedUser.createdAt}</span>
                      </div>
                      {selectedUser.lastLogin && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Last Login</span>
                          <span className="text-sm text-gray-900">{selectedUser.lastLogin}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl">
                <div className="flex justify-end space-x-3">
                  <Button
                    onClick={() => handleResetPassword(selectedUser)}
                    variant="secondary"
                  >
                    <ArrowPathIcon className="h-4 w-4 mr-2" />
                    Reset Password
                  </Button>
                  <Button
                    onClick={() => handleEditUser(selectedUser)}
                    className="bg-primary-600 hover:bg-primary-700"
                  >
                    <PencilIcon className="h-4 w-4 mr-2" />
                    Edit User
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default UsersPage